import { useRef, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FiUploadCloud, FiX, FiLoader } from "react-icons/fi";
import CustomButton from "../../components/common/CustomButton";
import { editProfileSchema } from "../../schemas/vendorProfileSchema";
import {
  useVendorProfile,
  useUpdateVendorProfile,
} from "../../custom-hooks/queries";
import { uploadFile } from "../../utils/HelperMethod";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

export default function EditProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: profileData, isLoading: isLoadingProfile } = useVendorProfile();
  const updateProfileMutation = useUpdateVendorProfile();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(editProfileSchema),
    defaultValues: {
      businessName: "",
      contactName: "",
      email: "",
      taxId: "",
      licenseNumber: "",
      phone: "",
      description: "",
      businessAddress: "",
    },
  });

  const [logoFile, setLogoFile] = useState(null);
  const [governmentId, setGovernmentId] = useState(null);
  const [insuranceFile, setInsuranceFile] = useState(null);

  // Existing file URLs from API
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const [existingGovIdUrl, setExistingGovIdUrl] = useState(null);
  const [existingInsuranceUrl, setExistingInsuranceUrl] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const logoInputRef = useRef(null);

  // Populate form with existing data
  useEffect(() => {
    if (profileData) {
      const profile = profileData?.data || profileData;
      reset({
        businessName: profile.businessName || "",
        contactName: profile.contact_name || "",
        email: profile.email || "",
        taxId: profile.tax_id || "",
        licenseNumber: profile.businessLicenseNumber || "",
        phone: profile.phone || "",
        description: profile.businessDescription || "",
        businessAddress: profile.businessAddress || "",
      });

      setExistingLogoUrl(profile.logoUrl);
      setExistingGovIdUrl(profile.governmentIdUrl);
      setExistingInsuranceUrl(profile.liabilityInsuranceUrl);
    }
  }, [profileData, reset]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      // Upload new files if selected
      let logoUrl = existingLogoUrl;
      let governmentIdUrl = existingGovIdUrl;
      let liabilityInsuranceUrl = existingInsuranceUrl;

      if (logoFile) {
        const uploadedUrl = await uploadFile(logoFile, "vendorLogo");
        if (uploadedUrl) {
          logoUrl = uploadedUrl.split("?")[0];
        }
      }

      if (governmentId) {
        const uploadedUrl = await uploadFile(governmentId, "govId");
        if (uploadedUrl) {
          governmentIdUrl = uploadedUrl.split("?")[0];
        }
      }

      if (insuranceFile) {
        const uploadedUrl = await uploadFile(
          insuranceFile,
          "liabilityInsurance",
        );
        if (uploadedUrl) {
          liabilityInsuranceUrl = uploadedUrl.split("?")[0];
        }
      }

      const payload = {
        contact_name: data.contactName,
        phone: data.phone,
        business_name: data.businessName,
        business_description: data.description,
        business_address: data.businessAddress,
        business_license_number: data.licenseNumber,
        tax_id: data.taxId,
        logo_url: logoUrl,
        government_id_url: governmentIdUrl,
        liability_insurance_url: liabilityInsuranceUrl,
      };

      updateProfileMutation.mutate(payload, {
        onSuccess: () => {
          toast.success("Profile updated successfully!");
          queryClient.invalidateQueries(["vendorProfile"]);
          navigate("/vendor/profile");
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message || "Failed to update profile",
          );
        },
      });
    } catch (error) {
      toast.error("Failed to upload files. Please try again.");
      console.error("Upload error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/vendor/profile");
  };

  if (isLoadingProfile) {
    return (
      <div className="w-full px-4 sm:px-6 py-6 sm:py-10">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          Edit Profile
        </h1>
        <p className="text-gray-500 text-xs sm:text-sm mt-1">
          View your business profile information
        </p>

        <div className="mt-6 flex items-center justify-center py-20">
          <FiLoader className="animate-spin text-[#01B9C6]" size={32} />
        </div>
      </div>
    );
  }

  const isLoading = isSubmitting || updateProfileMutation.isPending;

  return (
    <div className="w-full px-4 sm:px-6 py-6 sm:py-10">
      {/* HEADER */}
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
        Edit Profile
      </h1>
      <p className="text-gray-500 text-xs sm:text-sm mt-1">
        Update your business profile information
      </p>

      {/* ACTION BUTTONS */}
      <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4 sm:mt-6">
        <button
          onClick={handleCancel}
          disabled={isLoading}
          className="border border-gray-300 text-gray-700 bg-white px-4 sm:px-6 py-2 rounded-lg hover:bg-gray-100 text-sm sm:text-base w-full sm:w-auto disabled:opacity-50"
        >
          Cancel
        </button>

        <CustomButton
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading}
          className="w-full sm:w-auto flex items-center justify-center gap-2"
        >
          {isLoading && <FiLoader className="animate-spin" size={16} />}
          {isLoading ? "Saving..." : "Save & Submit"}
        </CustomButton>
      </div>

      <div className="mt-4 sm:mt-6 grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-4 sm:gap-8">
        {/* ---------------- LEFT FORM ---------------- */}
        <div className="bg-white border rounded-2xl p-6 shadow-sm">
          {/* Business Name */}
          <FieldInput
            label="Business Name"
            placeholder="Enter business name"
            error={errors.businessName}
            register={register("businessName")}
          />

          {/* 2-col Row */}
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <FieldInput
              label="Contact Name"
              placeholder="Enter Your Full Name"
              error={errors.contactName}
              register={register("contactName")}
            />

            <FieldInput
              label="Email"
              placeholder="Enter Your Email Address"
              error={errors.email}
              register={register("email")}
              disabled
            />
          </div>

          {/* 2-col Row */}
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <FieldInput
              label="Tax ID"
              placeholder="123-45-6789"
              error={errors.taxId}
              register={register("taxId")}
            />

            <FieldInput
              label="Business License Number"
              placeholder="LIC-987267"
              error={errors.licenseNumber}
              register={register("licenseNumber")}
            />
          </div>

          {/* Phone */}
          <div className="mt-4">
            <FieldInput
              label="Phone"
              placeholder="+1 (555) 444 555 77"
              error={errors.phone}
              register={register("phone")}
            />
          </div>

          {/* Description */}
          <div className="mt-4">
            <label className="text-sm font-semibold text-gray-700">
              Business Description
            </label>

            <textarea
              {...register("description")}
              placeholder="Describe your business in detail..."
              className="mt-1 w-full border rounded-lg px-3 py-2.5 h-28 text-sm outline-none focus:border-[#01B9C6] resize-none"
            />
            {errors.description && (
              <p className="text-xs text-red-500 mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Address */}
          <div className="mt-4">
            <FieldInput
              label="Business Address"
              placeholder="Address"
              error={errors.businessAddress}
              register={register("businessAddress")}
            />
          </div>
        </div>

        {/* ---------------- RIGHT — LOGO ---------------- */}
        <div className="bg-white border rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col items-center max-h-none sm:max-h-[180px] order-first lg:order-last">
          {/* Logo preview */}
          <img
            src={
              logoFile
                ? URL.createObjectURL(logoFile)
                : existingLogoUrl || "https://i.pravatar.cc/200?img=12"
            }
            alt="Business Logo"
            className="h-20 w-20 rounded-full object-cover"
            onError={(e) => {
              e.target.src = "https://i.pravatar.cc/200?img=12";
            }}
          />

          {/* Change Logo */}
          <button
            type="button"
            onClick={() => logoInputRef.current.click()}
            className="mt-3 text-[#01B9C6] text-sm font-medium hover:underline"
          >
            Change Logo
          </button>

          <input
            type="file"
            ref={logoInputRef}
            className="hidden"
            accept="image/*"
            onChange={(e) => setLogoFile(e.target.files[0])}
          />
        </div>
      </div>

      {/* ---------------- FILE UPLOAD SECTIONS ---------------- */}
      <div className="mt-4 sm:mt-6 grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-4 sm:gap-8">
        <UploadSection
          title="Government ID"
          file={governmentId}
          setFile={setGovernmentId}
          existingUrl={existingGovIdUrl}
          onRemoveExisting={() => setExistingGovIdUrl(null)}
        />
      </div>
      <div className="mt-4 sm:mt-6 grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-4 sm:gap-8">
        <UploadSection
          title="Liability Insurance"
          file={insuranceFile}
          setFile={setInsuranceFile}
          existingUrl={existingInsuranceUrl}
          onRemoveExisting={() => setExistingInsuranceUrl(null)}
        />
      </div>
    </div>
  );
}

/* ---------------- REUSABLE INPUT COMPONENT ---------------- */
function FieldInput({ label, placeholder, register, error, disabled }) {
  return (
    <div>
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      <input
        {...register}
        placeholder={placeholder}
        disabled={disabled}
        className={`mt-1 w-full border rounded-lg px-3 py-2.5 text-sm outline-none 
          ${error ? "border-red-500" : "focus:border-[#01B9C6]"}
          ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error.message}</p>}
    </div>
  );
}

/* ---------------- FILE UPLOAD SECTION ---------------- */
function UploadSection({
  title,
  file,
  setFile,
  existingUrl,
  onRemoveExisting,
}) {
  // If there's an existing URL and no new file selected, show the existing file
  const hasExistingFile = existingUrl && !file;

  return (
    <div className="bg-white border rounded-2xl p-4 sm:p-6 shadow-sm">
      <p className="text-sm font-semibold text-gray-800">{title}</p>

      <div className="mt-3">
        {!file && !hasExistingFile ? (
          <label className="block border border-dashed rounded-xl px-4 py-6 text-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition">
            <input
              type="file"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0])}
            />
            <FiUploadCloud className="mx-auto text-gray-400" size={26} />
            <p className="text-sm text-gray-600 mt-2">
              Drop your file here or upload
            </p>
            <p className="text-xs text-gray-500">JPG/PNG/PDF up to 5Mb</p>
          </label>
        ) : hasExistingFile ? (
          <div className="flex items-center justify-between border rounded-xl px-4 py-3 bg-gray-50">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <FiUploadCloud size={18} className="text-[#01B9C6]" />
              <span className="truncate max-w-[200px]">
                {existingUrl.split("/").pop() || "Existing file"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <a
                href={existingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#01B9C6] text-sm hover:underline"
              >
                View
              </a>
              <button
                type="button"
                onClick={onRemoveExisting}
                className="text-red-500"
              >
                <FiX size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between border rounded-xl px-4 py-3 bg-gray-50">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <FiUploadCloud size={18} className="text-[#01B9C6]" />
              <span className="truncate max-w-[200px]">{file.name}</span>
            </div>

            <button
              type="button"
              onClick={() => setFile(null)}
              className="text-red-500"
            >
              <FiX size={20} />
            </button>
          </div>
        )}

        {/* Replace button when existing file is shown */}
        {hasExistingFile && (
          <label className="mt-2 block text-center">
            <input
              type="file"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0])}
            />
            <span className="text-[#01B9C6] text-sm font-medium cursor-pointer hover:underline">
              Replace with new file
            </span>
          </label>
        )}
      </div>
    </div>
  );
}
